import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTenantContext } from '@/hooks/useTenantContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CheckCircle2, XCircle, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export function TenantSyncStatus() {
  const { user, signOut } = useAuth();
  const { tenantId, tenantClaim, tenantSlug, role, isAssumed } = useTenantContext();

  const { data: dbCompany, isLoading } = useQuery({
    queryKey: ['company-verification', tenantId],
    queryFn: async () => {
      if (!tenantId) return null;

      const { data, error } = await supabase
        .from('companies')
        .select('id, name, public_key')
        .eq('id', tenantId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!tenantId,
  });

  const isInSync = dbCompany && tenantSlug === dbCompany.public_key;

  const handleForceRefresh = async () => {
    toast.info('Logging out to refresh JWT claims...');
    await signOut();
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Tenant Sync Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground">Checking...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Tenant Sync Status</CardTitle>
          {isInSync ? (
            <Badge variant="success">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              In Sync
            </Badge>
          ) : (
            <Badge variant="destructive">
              <XCircle className="h-3 w-3 mr-1" />
              Out of Sync
            </Badge>
          )}
        </div>
        <CardDescription>Compare JWT claims with database reality</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="font-semibold">Property</div>
            <div className="font-semibold">JWT Claims</div>
            <div className="font-semibold">Database</div>

            <div className="text-muted-foreground">Tenant ID</div>
            <div className="font-mono text-xs">{tenantClaim || 'None'}</div>
            <div className="font-mono text-xs">{dbCompany?.id || 'Not found'}</div>

            <div className="text-muted-foreground">Tenant Slug</div>
            <div className="font-mono">{tenantSlug || 'None'}</div>
            <div className="font-mono">{dbCompany?.public_key || 'Not found'}</div>

            <div className="text-muted-foreground">Company Name</div>
            <div className="text-muted-foreground text-sm">-</div>
            <div>{dbCompany?.name || 'Not found'}</div>

            <div className="text-muted-foreground">Role</div>
            <div>
              <Badge variant="outline">{role || 'None'}</Badge>
            </div>
            <div className="text-muted-foreground text-sm">-</div>

            <div className="text-muted-foreground">Is Assumed</div>
            <div>
              {isAssumed ? (
                <Badge variant="secondary">Yes</Badge>
              ) : (
                <span className="text-muted-foreground text-sm">No</span>
              )}
            </div>
            <div className="text-muted-foreground text-sm">-</div>
          </div>
        </div>

        {!isInSync && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Sync Issue Detected</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>
                Your JWT claims show tenant slug "{tenantSlug}" but the database shows "{dbCompany?.public_key}".
                This happens after company code migration.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleForceRefresh}
                className="mt-2"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Force JWT Refresh (Logout & Login)
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <div className="text-xs text-muted-foreground">
          <p>User ID: {user?.id}</p>
          <p>Email: {user?.email}</p>
        </div>
      </CardContent>
    </Card>
  );
}
